import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

// ═══════════════════════════════════════════════════════════════════════
// SEARCHABLE SELECT — a combobox used for anything with too many options
// to sit comfortably in a plain <select>. Renders the panel through a
// portal into document.body, sized and positioned from the trigger's own
// rect, so it never gets clipped by a modal's `overflow-y-auto` body and
// never fights that container for width.
// `groups` (optgroup-style: [{label, options:[{value,label,sub}]}]) and
// flat `options` are mutually exclusive. `footer` renders pinned content
// below the list (used for "+ Create new category").
// ═══════════════════════════════════════════════════════════════════════
export default function SearchableSelect({ value, options, groups, onChange, placeholder = 'Select…', footer }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  const flatOptions = groups ? groups.flatMap(g => g.options) : (options || []);
  const selected = flatOptions.find(o => String(o.value) === String(value));

  const openPanel = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const panelHeight = 280;
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow < panelHeight && rect.top > panelHeight ? rect.top - panelHeight - 6 : rect.bottom + 6;
      setPos({ top, left: rect.left, width: rect.width });
    }
    setQuery('');
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => searchRef.current?.focus());
    const handleClick = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const matches = (opt) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return opt.label.toLowerCase().includes(q) || (opt.sub || '').toLowerCase().includes(q);
  };

  const selectOpt = (opt) => { onChange(opt.value); setOpen(false); };

  return (
    <div style={{ position: 'relative', minWidth: 0 }}>
      <button type="button" ref={btnRef} onClick={() => (open ? setOpen(false) : openPanel())}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 bg-white flex items-center justify-between gap-2"
        style={{ minWidth: 0 }}>
        <span className={`truncate text-left ${selected ? 'text-gray-800' : 'text-gray-400'}`}>{selected ? selected.label : placeholder}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0, color: '#94a3b8' }}><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      {open && ReactDOM.createPortal(
        <div ref={panelRef} style={{ position: 'fixed', top: pos.top, left: pos.left, width: Math.max(pos.width, 260), background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.18)', zIndex: 9999, display: 'flex', flexDirection: 'column', maxHeight: 280 }}>
          <div className="p-2 border-b border-gray-100">
            <input ref={searchRef} value={query} onChange={e => setQuery(e.target.value)} placeholder="Search…"
              className="w-full border border-gray-200 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:border-blue-500" />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {groups ? (
              groups.map(g => {
                const items = g.options.filter(matches);
                if (items.length === 0) return null;
                return (
                  <div key={g.label}>
                    <p className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wide text-gray-400 sticky top-0 bg-white">{g.label}</p>
                    {items.map(opt => (
                      <button type="button" key={opt.value} onClick={() => selectOpt(opt)}
                        className={`w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 ${String(opt.value) === String(value) ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                );
              })
            ) : (
              flatOptions.filter(matches).map(opt => (
                <button type="button" key={opt.value} onClick={() => selectOpt(opt)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${String(opt.value) === String(value) ? 'bg-blue-50' : ''}`}>
                  <p className={`font-medium ${String(opt.value) === String(value) ? 'text-blue-700' : 'text-gray-800'}`}>{opt.label}</p>
                  {opt.sub && <p className="text-[11px] text-gray-400">{opt.sub}</p>}
                </button>
              ))
            )}
            {((groups ? groups.every(g => g.options.filter(matches).length === 0) : flatOptions.filter(matches).length === 0)) && (
              <p className="px-3 py-4 text-xs text-gray-400 text-center">No matches</p>
            )}
          </div>
          {footer && <div className="border-t border-gray-100">{footer({ close: () => setOpen(false) })}</div>}
        </div>,
        document.body
      )}
    </div>
  );
}
